import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useRouterContext } from "@repo/next-utils/router";
import * as Flex from "@repo/ui/flex";
import * as PaginationComponent from "@repo/ui/pagination";
import { VisuallyHidden } from "@repo/ui/visually-hidden";

import { ChevronLeft } from "@/theme/icons/chevron-left";
import { ChevronRight } from "@/theme/icons/chevron-right";

import { TYPOGRAPHY_LABEL3_SENTENCE } from "@/theme/topshop-2/typography/typography-constants";
import { isPageRouter } from "@/utils/is-page-router";
import classes from "./pagination.module.css";

export type PaginationProps = Flex.FlexContainerProps & {
  pages: PaginationComponent.PaginationProps["pages"];
  currentPage: number;
  searchParams: URLSearchParams;
};

export const Pagination = ({
  currentPage,
  pages,
  searchParams,
  ...props
}: PaginationProps) => {
  const firstPageNumber = pages[0]?.number;
  const lastPageNumber = pages[pages.length - 1]?.number;
  const isFirstPage = currentPage === firstPageNumber;
  const isLastPage = currentPage === lastPageNumber;
  const isSinglePage = pages.length <= 1;
  const { appRouterPathPrefixes } = useRouterContext();

  const pathname = usePathname();

  const pageRouter = isPageRouter(pathname, appRouterPathPrefixes);

  const getAdjacentPageHref = (type: "previous" | "next") => {
    const adjacentPage =
      type === "previous" ? currentPage - 1 : currentPage + 1;
    const query = Object.fromEntries(searchParams.entries());
    query.page = adjacentPage.toString();

    return { pathname: pageRouter ? pathname : null, query };
  };

  const getPageHref = (page: number | undefined) => {
    if (!page) return { pathname: null, query: {} };

    const query = Object.fromEntries(searchParams.entries());
    if (page === 1) {
      delete query.page;
    } else {
      query.page = page.toString();
    }
    return { pathname: pageRouter ? pathname : null, query };
  };

  return (
    <Flex.Container
      gap="xs"
      justify="center"
      align="center"
      asChild
      className={clsx(classes.container, props.className)}
      {...props}
    >
      <nav aria-label="Pagination">
        <PaginationComponent.Previous
          linkProps={{
            href: isFirstPage ? "" : getAdjacentPageHref("previous"),
          }}
          disabled={isFirstPage}
          tabIndex={isFirstPage ? -1 : undefined}
          className={clsx(
            classes.paginationControl,
            isFirstPage && classes.disabled,
          )}
        >
          <Flex.Container align="center" justify="center" asChild>
            <ChevronLeft />
          </Flex.Container>
          <VisuallyHidden>Previous Page</VisuallyHidden>
        </PaginationComponent.Previous>
        <PaginationComponent.First
          linkProps={{ href: getPageHref(firstPageNumber) }}
          className={clsx(classes.paginationItem, TYPOGRAPHY_LABEL3_SENTENCE)}
          data-current-page={isFirstPage}
          aria-current={isFirstPage ? "page" : undefined}
          aria-label={isFirstPage ? "Current page, page 1" : "First page"}
        >
          {firstPageNumber}
        </PaginationComponent.First>
        {!isSinglePage ? (
          <>
            <PaginationComponent.Pagination
              pages={pages}
              currentPage={currentPage}
              prefetching={true}
              gap="xs"
              itemProps={{
                className: clsx(
                  classes.paginationItem,
                  TYPOGRAPHY_LABEL3_SENTENCE,
                ),
              }}
            />
            <PaginationComponent.Last
              linkProps={{ href: getPageHref(lastPageNumber) }}
              className={clsx(
                classes.paginationItem,
                TYPOGRAPHY_LABEL3_SENTENCE,
              )}
              data-current-page={isLastPage}
              aria-current={isLastPage ? "page" : undefined}
              aria-label={
                isLastPage
                  ? `Current page, page ${lastPageNumber}`
                  : "Last page"
              }
            >
              {lastPageNumber}
            </PaginationComponent.Last>
          </>
        ) : null}
        <PaginationComponent.Next
          linkProps={{
            href: isLastPage ? "" : getAdjacentPageHref("next"),
          }}
          disabled={isLastPage}
          tabIndex={isLastPage ? -1 : undefined}
          className={clsx(
            classes.paginationControl,
            isLastPage && classes.disabled,
          )}
        >
          <Flex.Container align="center" justify="center" asChild>
            <ChevronRight />
          </Flex.Container>
          <VisuallyHidden>Next Page</VisuallyHidden>
        </PaginationComponent.Next>
      </nav>
    </Flex.Container>
  );
};
